/* ═══════════════════════════════════════════════════════════════════
   PacketVoyager — Shared constants
   ═══════════════════════════════════════════════════════════════════ */

/* ─────────────── Hop / marker colors ─────────────── */

export const HOP_COLORS = {
  origin: "#FFA726",
  transit: "#06B6D4",
  ixp: "#8B5CF6",
  cable: "#06B6D4",
  destination: "#10B981",
} as const;

export const HOP_RING_COLORS = {
  origin: "border-[#FFA726]/30",
  transit: "border-[#06B6D4]/30",
  ixp: "border-[#8B5CF6]/30",
  cable: "border-[#06B6D4]/30",
  destination: "border-[#10B981]/30",
} as const;

export const HOP_BG_COLORS = {
  origin: "bg-[#FFA726]",
  transit: "bg-[#06B6D4]",
  ixp: "bg-[#8B5CF6]",
  cable: "bg-[#06B6D4]",
  destination: "bg-[#10B981]",
} as const;

/* ─────────────── Latency thresholds (ms) ─────────────── */

export const LATENCY = {
  GOOD: 5,
  MODERATE: 15,
  SLOW: 50,
} as const;

export const LATENCY_COLORS = {
  excellent: "#10B981",
  good: "#06B6D4",
  moderate: "#FFA726",
  poor: "#F43F5E",
} as const;

/* ─────────────── Speed-of-light physics ─────────────── */

export const PHYSICS = {
  SPEED_OF_LIGHT_VACUUM: 299_792, // km/s
  SPEED_OF_LIGHT_FIBER: 200_000,  // km/s (~2/3 of vacuum)
} as const;

/* ─────────────── Animation timings (ms) ─────────────── */

export const ANIMATION = {
  LOADING_DELAY: 800,          // loading → animating transition
  CABLE_HOP_DELAY: 2500,       // base delay for cable crossings
  FAST_HOP_DELAY: 600,         // base delay for local hops (<10ms latency)
  NORMAL_HOP_DELAY: 1000,      // base delay for regular hops
} as const;

export const SPEED_MULTIPLIERS: Record<string, number> = {
  narrative: 1,
  fast: 0.3,
  realtime: 0.01,
} as const;

/* ─────────────── Camera settings ─────────────── */

export const CAMERA = {
  INITIAL_LAT: 20,
  INITIAL_LNG: 75,
  ORBIT_ALTITUDE: 2.5,
  DEPARTURE_ALTITUDE: 1.2,
  FOLLOW_ALTITUDE: 1.8,
  WHALE_ALTITUDE: 3.0,
  ARRIVAL_ALTITUDE: 1.2,
  REVEAL_ALTITUDE: 3.5,

  DEPARTURE_MS: 1200,
  FOLLOW_MS: 800,
  WHALE_MS: 1500,
  ARRIVAL_MS: 1000,
  REVEAL_MS: 3000,
  ORBIT_MS: 2000,

  // Lead camera: 30% current, 70% next
  LEAD_CURRENT: 0.3,
  LEAD_NEXT: 0.7,
} as const;

/* ─────────────── Globe controls ─────────────── */

export const GLOBE_CONTROLS = {
  MIN_DISTANCE: 120,
  MAX_DISTANCE: 600,
  ROTATE_SPEED: 0.4,
  ZOOM_SPEED: 0.8,
  DAMPING_FACTOR: 0.08,
  AUTO_ROTATE_SPEED: 0.3,
} as const;

/* ─────────────── Globe material ─────────────── */

export const GLOBE_MATERIAL = {
  BUMP_SCALE: 3,
  SHININESS: 3,
  SPECULAR_COLOR: 0x111122,
  TONE_MAPPING_EXPOSURE: 1.0,
} as const;

/* ─────────────── Layer toggle colors ─────────────── */

export const LAYER_COLORS = {
  cables: "#06B6D4",   // cyan — submarine cables
  infra: "#8B5CF6",    // purple — infrastructure beacons
  arcs: "#FFA726",     // amber — hop-to-hop arcs
  events: "#10B981",   // green — protocol events
} as const;

/* ─────────────── Panel styling ─────────────── */

export const PANEL = {
  BG: "rgba(13, 13, 26, 0.85)",
  BG_HEAVY: "rgba(13, 13, 26, 0.92)",
  BORDER: "rgba(255, 255, 255, 0.08)",
  BORDER_HOVER: "rgba(255, 255, 255, 0.16)",
  SHADOW: "0 4px 20px rgba(0,0,0,0.4)",
  SHADOW_HEAVY: "0 8px 40px rgba(0,0,0,0.5)",
} as const;
